import { useEffect, useMemo, useRef } from 'react'

import { basicSetup } from '@codemirror/basic-setup'
import { css } from '@codemirror/lang-css'
import { html } from '@codemirror/lang-html'
import { syntaxHighlighting, defaultHighlightStyle } from '@codemirror/language'
import { linter, Diagnostic, lintGutter } from '@codemirror/lint'
import { Annotation, Text } from '@codemirror/state'
import { EditorView, EditorViewConfig, ViewUpdate } from '@codemirror/view'
import { HTMLHint, LintResult } from 'htmlhint'

const External = Annotation.define<boolean>()

export type CodeEditorProps = {
  onChange: (newValue: string, hasError: boolean) => void
  disabled?: boolean
  initialValue?: string
  placeholder?: string
}

// HTMLHint seems to have incomplete type definitions
type HTMLHintMessage = LintResult & { type?: string }

const HTML_LINT_RULE_SET = {
  'tagname-lowercase': true,
  'tag-pair': true,
}

const posToOffset = (
  doc: Text,
  { line, col }: { line: number; col: number },
) => {
  return doc.line(line + 1).from + col
}

const htmlLinter = linter(view => {
  const text = view.state.doc.toString()
  const diagnostics: Diagnostic[] = []

  if (!HTMLHint) {
    return diagnostics
  }
  const messages: HTMLHintMessage[] = HTMLHint.verify(text, HTML_LINT_RULE_SET)
  for (let i = 0; i < messages.length; i++) {
    const message = messages[i]
    const startLine = message.line - 1
    const endLine = message.line - 1
    const startCol = message.col - 1
    const endCol = message.col
    diagnostics.push({
      from: posToOffset(view.state.doc, { line: startLine, col: startCol }),
      to: posToOffset(view.state.doc, { line: endLine, col: endCol }),
      message: message.message,
      severity: message.type,
    })
  }
  return diagnostics
})

const editorFromTextArea = (
  textarea: HTMLTextAreaElement,
  extensions: EditorViewConfig['extensions'],
) => {
  const view = new EditorView({ doc: textarea.value, extensions })
  textarea.parentNode.insertBefore(view.dom, textarea)
  textarea.style.display = 'none'
  if (textarea.form) {
    textarea.form.addEventListener('submit', () => {
      textarea.value = view.state.doc.toString()
    })
  }
  return view
}

export const CodeEditor = ({
  onChange,
  disabled,
  initialValue,
  placeholder,
}: CodeEditorProps) => {
  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  const cmInstance = useRef<EditorView>()

  const updateListener = useMemo(
    () =>
      EditorView.updateListener.of((vu: ViewUpdate) => {
        if (
          vu.docChanged &&
          typeof onChange === 'function' &&
          // Fix echoing of the remote changes:
          // If transaction is market as remote we don't have to call `onChange` handler again
          !vu.transactions.some(tr => tr.annotation(External))
        ) {
          const { doc } = vu.state
          const text = doc.toString()
          const errors: HTMLHintMessage[] = HTMLHint.verify(
            text,
            HTML_LINT_RULE_SET,
          )
          const hasError = Boolean(
            errors?.filter(error => error.type === 'error')?.length,
          )
          onChange(text, hasError)
        }
      }),
    [onChange],
  )

  useEffect(() => {
    const theme = EditorView.baseTheme({
      '.cm-content, .cm-gutter': { minHeight: '300px' },
    })

    cmInstance.current = editorFromTextArea(textAreaRef.current, [
      basicSetup,
      css(),
      html(),
      syntaxHighlighting(defaultHighlightStyle),
      theme,
      htmlLinter,
      lintGutter(),
      updateListener,
    ])
  }, [updateListener])

  return (
    <div className="border-2 rounded overflow-hidden min-h-80">
      <textarea
        ref={textAreaRef}
        defaultValue={initialValue}
        disabled={disabled}
        placeholder={placeholder}
      />
    </div>
  )
}
